import { useState } from 'react';
import { toast } from 'react-toastify';
import ReuseableTable from '../../table/ReuseableTable';
import useGetMarketingData from '../../../hooks/marketing/useGetMarketingData';
import { createRow, headerCells } from '../../../utils/table/campaignsTable';
import CampaignSideBarModal from '../CampaignSideBarModal';
import CampaignEditorModal from '../CampaignEditorModal';
import { MarketingCampaignWithSource } from '../../../types/marketing';
import useDeleteCampaign from '../../../hooks/marketing/useDeleteCampaign';
import useConfirm from '../../../hooks/useConfirm';
import useLoadingModal from '../../../hooks/global/useLoadingModal';
import { Optional } from '../../../types/global';
import useStopCampaign from '../../../hooks/marketing/useStopCampaign';
import { campaignHasEmailStatistics } from '../../../helpers/marketing';

function CampaignsTable() {
  const confirm = useConfirm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();

  const [selectedViewCampaignId, setSelectedViewCampaignId] = useState<
    string | null
  >(null);

  const [selectedEditCampaign, setSelectedEditCampaign] = useState<Optional<
    MarketingCampaignWithSource,
    'id'
  > | null>(null);

  const { data, isLoading } = useGetMarketingData();
  const { mutate: deleteCampaign, isPending: isDeleting } = useDeleteCampaign({
    onMutate: () => {
      openLoadingModal({
        header: 'Deleting campaign',
        message: 'Please wait while the campaign is being deleted',
      });
    },
    onSettled: () => {
      closeLoadingModal();
    },
  });
  const { mutate: stopCampaign, isPending: isStopping } = useStopCampaign({
    onMutate: () => {
      openLoadingModal({
        header: 'Stopping campaign',
        message: 'Please wait while the campaign is being stopped',
      });
    },
    onSettled: () => {
      closeLoadingModal();
    },
    onError: () => {
      toast.error('Failed to stop campaign');
    },
  });

  const selectedViewCampaign =
    data?.campaigns?.find(campaign => campaign.id === selectedViewCampaignId) ||
    null;

  const onDeleteCampaign = async (campaignId: string) => {
    const confirmed = await confirm({
      title: 'Delete campaign',
      message:
        'Are you sure you want to delete this campaign? This action cannot be undone.',
      positiveText: 'Delete',
    });
    if (!confirmed) return;

    deleteCampaign(campaignId);
  };

  const onStopCampaign = async (campaignId: string) => {
    const confirmed = await confirm({
      title: 'Stop campaign',
      message:
        'Are you sure you want to stop this campaign? This action cannot be undone. If you purchased credits for this campaign, the credits will be available for future campaigns.',
      positiveText: 'Stop',
    });
    if (!confirmed) return;

    stopCampaign(campaignId);
  };

  const hasEmailStatistics = data?.campaigns?.some(c =>
    campaignHasEmailStatistics(c),
  );
  const filteredHeaderCells = headerCells.filter(
    cell => cell.id !== 'analytics' || hasEmailStatistics,
  );

  return (
    <>
      <ReuseableTable
        name="Campaigns"
        emptyMessage="No campaigns have been created yet"
        headerCells={filteredHeaderCells}
        loading={isLoading}
        rows={
          data?.campaigns?.map(campaign =>
            createRow(campaign, {
              onSelectCampaign: setSelectedViewCampaignId,
              onEditCampaign: campaignToEdit =>
                setSelectedEditCampaign(campaignToEdit),
              onDuplicateCampaign: campaignToDupe =>
                setSelectedEditCampaign({ ...campaignToDupe, id: undefined }),
              onDeleteCampaign,
              onStopCampaign,
              disableActions: isDeleting || isStopping,
              hasEmailStatistics,
            }),
          ) ?? []
        }
      />
      <CampaignSideBarModal
        campaign={selectedViewCampaign}
        isOpen={!!selectedViewCampaignId}
        onClose={() => setSelectedViewCampaignId(null)}
      />
      <CampaignEditorModal
        key={selectedEditCampaign?.id ?? 'closed'}
        isOpen={!!selectedEditCampaign}
        onClose={() => {
          setSelectedEditCampaign(null);
        }}
        campaign={selectedEditCampaign}
      />
    </>
  );
}

export default CampaignsTable;
